import { type ReactNode } from "react";
import { Link } from "react-router-dom";

import { CommitPopover } from "~/components/CommitPopover";
import type {
  Cluster,
  FleetOperation,
  GitCommitDetails,
  LatestDeployed,
  ResolvedFleet,
} from "~/types";
import { formatGitSha, pluralize } from "~/utils";
import {
  clustersOnCommit,
  fleetOperationProgress,
  waveClusterCount,
} from "~/utils/fleet";

/** Fleet upgrades the strip cares about; other fleet operations (restarts,
 * ...) live on /fleet-ops only. */
function activeFleetUpgrades(operations: FleetOperation[]): FleetOperation[] {
  return operations.filter(
    (operation) => operation.operation_name === "upgrade",
  );
}

/** The fleet upgrade strip above the cluster list: a green action line when
 * an upgrade is available, a progress line while one runs (same bar idiom as
 * the fleet-ops page), nothing when the fleet is current. */
export function FleetStatus({
  activeOperations,
  deployedFleet,
  masterFleet,
  latest,
  clusters,
  onUpgrade,
}: {
  activeOperations: FleetOperation[];
  deployedFleet: ResolvedFleet | null;
  masterFleet: ResolvedFleet | null;
  latest: LatestDeployed | null;
  clusters: Cluster[];
  onUpgrade: (target: "deployed" | "master") => void;
}) {
  const deployed = latest?.commit ?? null;
  const master = latest?.master ?? null;
  const upgrades = activeFleetUpgrades(activeOperations);

  if (upgrades.length > 1) {
    const text = `${upgrades.length} fleet operations in flight`;
    return (
      <Link
        to="/fleet-ops"
        aria-label={`${text} → view`}
        className="group mb-2 flex items-baseline gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 font-mono text-xs text-blue-700 hover:border-blue-300 hover:text-blue-900"
      >
        <span className="h-1.5 w-1.5 shrink-0 animate-pulse self-center rounded-full bg-blue-500" />
        {text}
        <span className="ml-auto group-hover:underline">view →</span>
      </Link>
    );
  }

  if (upgrades.length === 1) {
    const operation = upgrades[0];
    const progress = operation.children
      ? fleetOperationProgress(operation, operation.children)
      : null;
    const progressText = progress
      ? ` · wave ${progress.currentWave}/${progress.totalWaves} · ${progress.done}/${progress.total} clusters`
      : "";
    const targetSha = operation.arguments.target_version;
    const label = `fleet upgrade in flight${progressText} → view`;
    return (
      <Link
        to={`/fleet-ops#op-${operation.id}`}
        aria-label={label}
        title={label}
        className="group mb-2 block rounded-md border border-blue-200 bg-blue-50 px-3 py-2 font-mono text-xs hover:border-blue-300"
      >
        <div className="flex items-baseline gap-2 pb-1.5 text-blue-700 group-hover:text-blue-900">
          <span className="h-1.5 w-1.5 shrink-0 animate-pulse self-center rounded-full bg-blue-500" />
          <span>
            upgrade
            {typeof targetSha === "string"
              ? ` → ${formatGitSha(targetSha)}`
              : ""}
            {progressText}
          </span>
          <span className="ml-auto group-hover:underline">view →</span>
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress?.total ?? 0}
          aria-valuenow={progress?.done ?? 0}
        >
          <div
            className="h-full animate-pulse bg-blue-500"
            style={{
              width: progress
                ? `${Math.max(4, (progress.done / Math.max(progress.total, 1)) * 100)}%`
                : "4%",
            }}
          />
        </div>
      </Link>
    );
  }

  const deployedCount = waveClusterCount(deployedFleet);
  const masterCount = waveClusterCount(masterFleet);

  const strip = (body: ReactNode, actions: ReactNode) => (
    <div className="mb-2 flex w-full flex-wrap items-baseline gap-2 overflow-visible rounded-md border border-green-200 bg-green-50 px-3 py-2 font-mono text-xs text-green-700">
      <span className="h-1.5 w-1.5 shrink-0 self-center rounded-full bg-green-500" />
      <span className="min-w-0">{body}</span>
      <span className="ml-auto flex flex-wrap items-baseline justify-end gap-3">
        {actions}
      </span>
    </div>
  );

  const shaAction = (
    verb: string,
    commit: GitCommitDetails,
    target: "deployed" | "master",
  ) => (
    <CommitPopover
      commit={commit}
      onClusters={
        target === "deployed"
          ? (latest?.on_clusters ?? [])
          : clustersOnCommit(clusters, commit.sha).map((cluster) => cluster.id)
      }
      align={target === "deployed" ? "start" : "end"}
    >
      <button
        type="button"
        onClick={() => onUpgrade(target)}
        className="hover:text-green-900 hover:underline"
      >
        {verb} {formatGitSha(commit.sha)} →
      </button>
    </CommitPopover>
  );

  if (deployed && deployedCount > 0) {
    return strip(
      <>
        fleet upgrade available · {pluralize(deployedCount, "cluster")} behind
      </>,
      <>
        {shaAction("catch up", deployed, "deployed")}
        {master && master.sha !== deployed.sha && masterCount > 0
          ? shaAction("master", master, "master")
          : null}
      </>,
    );
  }

  if (master && masterCount > 0) {
    return strip(
      <>latest on master · {pluralize(masterCount, "cluster")}</>,
      shaAction("deploy", master, "master"),
    );
  }

  return null;
}
